import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { TutorResponse, ImageSolveResponse, Approach, Step, RelatedFormula, VisualElements } from '../../types';

interface TutorState {
  // Question state
  currentQuestion: string;
  questionInput: string;
  questionExplanation: string;
  topic: string;
  subtopic: string;
  
  // Solution data
  approaches: Approach[];
  currentApproach: number;
  currentStep: number;
  
  // Additional solution metadata
  relatedFormulas: RelatedFormula[];
  finalAnswer: string;
  verification: string;
  
  // Loading & Error states
  isLoading: boolean;
  error: string | null;
  
  // Speech state
  isSpeaking: boolean;

  // Image upload state
  isImageUploading: boolean;
  extractedText: string;
  imageUploadError: string | null;

  // Visual elements from image (diagrams, tables, graphs, coordinates, measurements)
  imageUrl: string;
  visualElements: VisualElements | null;
}

const initialState: TutorState = {
  currentQuestion: '',
  questionInput: '',
  questionExplanation: '',
  topic: '',
  subtopic: '',
  approaches: [],
  currentApproach: 0,
  currentStep: 0,
  relatedFormulas: [],
  finalAnswer: '',
  verification: '',
  isLoading: false,
  error: null,
  isSpeaking: false,
  isImageUploading: false,
  extractedText: '',
  imageUploadError: null,
  imageUrl: '',
  visualElements: null,
};

const tutorSlice = createSlice({
  name: 'tutor',
  initialState,
  reducers: {
    // Question actions
    setQuestionInput: (state, action: PayloadAction<string>) => {
      state.questionInput = action.payload;
    },
    
    // API call actions
    fetchSolutionRequest: (state, action: PayloadAction<string>) => {
      state.isLoading = true;
      state.error = null;
      state.currentQuestion = action.payload;
      state.questionInput = '';
      state.extractedText = '';
      state.imageUrl = '';
      state.visualElements = null;
      state.imageUploadError = null;
    },
    
    fetchSolutionSuccess: (state, action: PayloadAction<TutorResponse>) => {
      state.isLoading = false;
      state.approaches = action.payload.data.approaches;
      state.currentQuestion = action.payload.data.questionPrompt;
      state.questionExplanation = action.payload.data.questionExplanation || '';
      state.topic = action.payload.data.topic || '';
      state.subtopic = action.payload.data.subtopic || '';
      state.relatedFormulas = action.payload.data.relatedFormulas || [];
      state.finalAnswer = action.payload.data.finalAnswer || '';
      state.verification = action.payload.data.verification || '';
      state.currentApproach = 0;
      state.currentStep = 0;
      state.error = null;
    },
    
    fetchSolutionFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    
    // Navigation actions
    setCurrentApproach: (state, action: PayloadAction<number>) => {
      state.currentApproach = action.payload;
      state.currentStep = 0;
    },
    
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    
    // Speech actions
    setIsSpeaking: (state, action: PayloadAction<boolean>) => {
      state.isSpeaking = action.payload;
    },
    
    // Reset state
    resetTutor: () => initialState,

    // Image upload actions
    setImageUploading: (state, action: PayloadAction<boolean>) => {
      state.isImageUploading = action.payload;
      if (action.payload) {
        state.imageUploadError = null;
        state.extractedText = '';
      }
    },

    fetchSolutionFromImageSuccess: (state, action: PayloadAction<ImageSolveResponse>) => {
      state.isImageUploading = false;
      state.isLoading = false;
      state.extractedText = action.payload.extractedText || '';
      state.imageUrl = action.payload.imageUrl || '';
      state.visualElements = action.payload.visualElements || null;
      state.approaches = action.payload.data.approaches;
      state.currentQuestion = action.payload.data.questionPrompt;
      state.questionExplanation = action.payload.data.questionExplanation || '';
      state.topic = action.payload.data.topic || '';
      state.subtopic = action.payload.data.subtopic || '';
      state.relatedFormulas = action.payload.data.relatedFormulas || [];
      state.finalAnswer = action.payload.data.finalAnswer || '';
      state.verification = action.payload.data.verification || '';
      state.currentApproach = 0;
      state.currentStep = 0;
      state.error = null;
      state.imageUploadError = null;
    },

    fetchSolutionFromImageFailure: (state, action: PayloadAction<string>) => {
      state.isImageUploading = false;
      state.imageUploadError = action.payload;
    },

    clearExtractedText: (state) => {
      state.extractedText = '';
      state.imageUrl = '';
      state.visualElements = null;
    },
  },
});

export const {
  setQuestionInput,
  fetchSolutionRequest,
  fetchSolutionSuccess,
  fetchSolutionFailure,
  setCurrentApproach,
  setCurrentStep,
  setIsSpeaking,
  resetTutor,
  setImageUploading,
  fetchSolutionFromImageSuccess,
  fetchSolutionFromImageFailure,
  clearExtractedText,
} = tutorSlice.actions;

export default tutorSlice.reducer;

// Selectors
export const selectCurrentQuestion = (state: { tutor: TutorState }) => state.tutor.currentQuestion;
export const selectQuestionInput = (state: { tutor: TutorState }) => state.tutor.questionInput;
export const selectApproaches = (state: { tutor: TutorState }) => state.tutor.approaches;
export const selectCurrentApproach = (state: { tutor: TutorState }) => state.tutor.currentApproach;
export const selectCurrentStep = (state: { tutor: TutorState }) => state.tutor.currentStep;
export const selectIsLoading = (state: { tutor: TutorState }) => state.tutor.isLoading;
export const selectError = (state: { tutor: TutorState }) => state.tutor.error;
export const selectIsSpeaking = (state: { tutor: TutorState }) => state.tutor.isSpeaking;
export const selectRelatedFormulas = (state: { tutor: TutorState }) => state.tutor.relatedFormulas;
export const selectFinalAnswer = (state: { tutor: TutorState }) => state.tutor.finalAnswer;
export const selectVerification = (state: { tutor: TutorState }) => state.tutor.verification;
export const selectQuestionExplanation = (state: { tutor: TutorState }) => state.tutor.questionExplanation;
export const selectTopic = (state: { tutor: TutorState }) => state.tutor.topic;
export const selectSubtopic = (state: { tutor: TutorState }) => state.tutor.subtopic;
export const selectIsImageUploading = (state: { tutor: TutorState }) => state.tutor.isImageUploading;
export const selectExtractedText = (state: { tutor: TutorState }) => state.tutor.extractedText;
export const selectImageUploadError = (state: { tutor: TutorState }) => state.tutor.imageUploadError;
export const selectImageUrl = (state: { tutor: TutorState }) => state.tutor.imageUrl;
export const selectVisualElements = (state: { tutor: TutorState }) => state.tutor.visualElements;

// Derived selectors
export const selectCurrentApproachData = (state: { tutor: TutorState }) => {
  const { approaches, currentApproach } = state.tutor;
  return approaches[currentApproach] || null;
};

export const selectSteps = (state: { tutor: TutorState }): Step[] => {
  const approach = selectCurrentApproachData(state);
  return approach?.steps || [];
};

export const selectCurrentStepData = (state: { tutor: TutorState }): Step | null => {
  const steps = selectSteps(state);
  const { currentStep } = state.tutor;
  return steps[currentStep] || null;
};
